import React from 'react';
import { visibleCustomSpecs } from '../../../utils/customSpecs.js';
import { formatInvoiceDate } from '../../../utils/invoiceAdjustments.js';
import '../../../component-styles/ProposalPdfDocument.css';

function fmt(value) {
  return Number(value || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function splitLines(value) {
  return String(value || '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function proxyImg(url, apiBase) {
  if (!url || !apiBase) return url;
  return `${apiBase}/api/upload/proxy-image?url=${encodeURIComponent(url)}`;
}

function formatDate(value) {
  if (!value) return '';
  return formatInvoiceDate(value) || String(value).slice(0, 10);
}

function formatDimensions(dim) {
  if (!dim || typeof dim !== 'object') return '';
  const parts = [];
  if (dim.width) parts.push(`${dim.width}"W`);
  if (dim.length) parts.push(`${dim.length}"L`);
  if (dim.height) parts.push(`${dim.height}"H`);
  if (dim.depth) parts.push(`${dim.depth}"D`);
  return parts.join(' x ');
}

function formatColorMaterial(it) {
  return [it.color, it.finishes, it.materials].filter(Boolean).join(' / ');
}

export default function ProposalPdfDocument({ proposalNumber, computedItems, totals, form, project, company, apiBase, template, notes }) {
  const logoUrl = proxyImg(company?.logoUrl, apiBase);
  const companyName = company?.companyName || 'InteriorFolio';
  const addressLines = splitLines(company?.address);
  const clientName = form?.client?.name || project?.customerName || '';
  const billingLines = splitLines(form?.billingAddress);
  const projectSite = form?.projectSite || '';

  const tpl = template || {};
  const showDimensions = tpl.showDimensions !== false;
  const showColorMaterial = tpl.showColorMaterial !== false;
  const showCustomSpecs = tpl.showCustomSpecs !== false;
  const showProductLink = tpl.showProductLink === true;
  const showMsrpSavings = tpl.showMsrpSavings === true;

  // totals.subtotal includes each line's shipping + tariff; list those separately.
  const shippingTotal = computedItems.reduce((sum, it) => sum + Number(it.clientShipping || 0), 0);
  const tariffTotal = computedItems.reduce((sum, it) => sum + Number(it.clientTariff || 0), 0);
  const merchSubtotal = Number(totals.subtotal || 0) - shippingTotal - tariffTotal;

  return (
    <div className="prop-pdf" style={{ '--pdf-accent': tpl.accentColor || '#a5d27d' }}>
      {/* ── HEADER ── */}
      <header className="prop-pdf__header">
        <div className="prop-pdf__brand">
          {logoUrl && <img className="prop-pdf__logo" src={logoUrl} alt="Company logo" crossOrigin="anonymous" />}
          <div className="prop-pdf__company-info">
            <div className="prop-pdf__company-name">{companyName}</div>
            {addressLines.map((line) => (
              <div key={line} className="prop-pdf__company-line">{line}</div>
            ))}
            {company?.contactNumber && <div className="prop-pdf__company-line">{company.contactNumber}</div>}
            {company?.email && <div className="prop-pdf__company-line">{company.email}</div>}
            {company?.website && <div className="prop-pdf__company-line">{company.website}</div>}
          </div>
        </div>
        <div>
          <div className="prop-pdf__doc-number">Proposal #{proposalNumber}</div>
          {form?.title && <div className="prop-pdf__doc-title">{form.title}</div>}
        </div>
      </header>

      <div className="prop-pdf__divider" />

      {/* ── PREPARED FOR + CONTACT ── */}
      <div className="prop-pdf__info-row">
        <div className="prop-pdf__bill-to">
          <div className="prop-pdf__info-label">Prepared for:</div>
          {clientName && <div className="prop-pdf__bill-name">{clientName}</div>}
          {billingLines.map((line) => (
            <div key={line} className="prop-pdf__bill-line">{line}</div>
          ))}
          {!billingLines.length && projectSite && (
            <div className="prop-pdf__bill-line">{projectSite}</div>
          )}
        </div>
        {(form?.client?.email || form?.date || form?.dueDate) && (
          <div className="prop-pdf__meta-dates">
            {form?.date && (
              <div className="prop-pdf__meta-row">
                <span className="prop-pdf__meta-label">Date:</span>
                <span>{formatDate(form.date)}</span>
              </div>
            )}
            {form?.dueDate && (
              <div className="prop-pdf__meta-row">
                <span className="prop-pdf__meta-label">Due Date:</span>
                <span>{formatDate(form.dueDate)}</span>
              </div>
            )}
            {form?.client?.email && (
              <div className="prop-pdf__meta-row">
                <span className="prop-pdf__meta-label">Email:</span>
                <span>{form.client.email}</span>
              </div>
            )}
            {project?.phone && (
              <div className="prop-pdf__meta-row">
                <span className="prop-pdf__meta-label">Phone:</span>
                <span>{project.phone}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="prop-pdf__divider" />

      {/* ── LINE ITEMS ── */}
      <section className="prop-pdf__items">
        <table className="prop-pdf__table">
          <thead>
            <tr>
              <th className="prop-pdf__col-img" />
              <th>Item</th>
              <th className="prop-pdf__col-room">Room</th>
              <th className="prop-pdf__col-num">Qty</th>
              <th className="prop-pdf__col-money">Unit Price</th>
              <th className="prop-pdf__col-money">Total</th>
            </tr>
          </thead>
          <tbody>
            {computedItems.map((it, idx) => {
              const itemName = it.clientName || it.name;
              // lineTotal includes the line's shipping + tariff, which only appear in the summary.
              const merchTotal = Number(it.lineTotal || 0) - Number(it.clientShipping || 0) - Number(it.clientTariff || 0);
              return (
              <tr key={it.itemId || idx}>
                <td className="prop-pdf__img-cell">
                  {it.imageUrl ? (
                    <img className="prop-pdf__item-img" src={proxyImg(it.imageUrl, apiBase) || it.imageUrl} alt="" crossOrigin="anonymous" />
                  ) : (
                    <div className="prop-pdf__img-placeholder" />
                  )}
                </td>
                <td>
                  <div className="prop-pdf__item-name">{itemName || 'Item'}</div>
                  {it.description && it.description !== itemName && (
                    <div className="prop-pdf__item-desc">{it.description}</div>
                  )}
                  {showDimensions && formatDimensions(it.dimensions) && (
                    <div className="prop-pdf__item-desc">{formatDimensions(it.dimensions)}</div>
                  )}
                  {showColorMaterial && formatColorMaterial(it) && (
                    <div className="prop-pdf__item-desc">{formatColorMaterial(it)}</div>
                  )}
                  {showCustomSpecs &&
                    visibleCustomSpecs(it.customSpecs).map((spec) => (
                      <div className="prop-pdf__item-desc" key={spec.id}>
                        {spec.label}{spec.value ? `: ${spec.value}` : ''}
                      </div>
                    ))}
                  {showProductLink && it.link && (
                    <div className="prop-pdf__item-link">
                      <a href={it.link} target="_blank" rel="noreferrer">View Product</a>
                    </div>
                  )}
                  {showMsrpSavings && Number(it.msrp) > Number(it.clientUnit) && (
                    <>
                      <div className="prop-pdf__item-msrp">MSRP: {fmt(it.msrp)}</div>
                      <div className="prop-pdf__item-savings">
                        You save {fmt((Number(it.msrp) - Number(it.clientUnit)) * Number(it.qty || 1))}
                      </div>
                    </>
                  )}
                </td>
                <td className="prop-pdf__col-room">{it.room || '-'}</td>
                <td className="prop-pdf__col-num">{Number(it.qty || 0).toFixed(2)}</td>
                <td className="prop-pdf__col-money">{fmt(it.clientUnit)}</td>
                <td className="prop-pdf__col-money">{fmt(merchTotal)}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {/* ── SUMMARY ── */}
      <section className="prop-pdf__totals">
        <div className="prop-pdf__totals-box">
          <div className="prop-pdf__totals-sep" />
          <div className="prop-pdf__totals-row">
            <span>Sub Total</span>
            <span>{fmt(merchSubtotal)}</span>
          </div>
          {shippingTotal > 0 && (
            <div className="prop-pdf__totals-row">
              <span>Shipping</span>
              <span>{fmt(shippingTotal)}</span>
            </div>
          )}
          {tariffTotal > 0 && (
            <div className="prop-pdf__totals-row">
              <span>Tariff</span>
              <span>{fmt(tariffTotal)}</span>
            </div>
          )}
          <div className="prop-pdf__totals-row">
            <span>Sales Tax</span>
            <span>{fmt(totals.tax)}</span>
          </div>
          {Number(totals.procurementFee) > 0 && (
            <div className="prop-pdf__totals-row">
              <span>Procurement Fee</span>
              <span>{fmt(totals.procurementFee)}</span>
            </div>
          )}
          <div className="prop-pdf__totals-sep prop-pdf__totals-sep--brand" />
          <div className="prop-pdf__totals-row prop-pdf__totals-row--total">
            <span>Total</span>
            <span>{fmt(totals.grandTotal)}</span>
          </div>
        </div>
      </section>

      {notes && (
        <section className="prop-pdf__notes">
          <div className="prop-pdf__notes-label">Notes / Payment Terms</div>
          <div className="prop-pdf__notes-text" dangerouslySetInnerHTML={{ __html: notes }} />
        </section>
      )}

      {tpl.termsText && (
        <section className="prop-pdf__terms">
          <div className="prop-pdf__terms-label">Terms &amp; Conditions</div>
          <div className="prop-pdf__terms-text" dangerouslySetInnerHTML={{ __html: tpl.termsText }} />
        </section>
      )}

      <div className="prop-pdf__footer">Proposal provided by InteriorFolio</div>
    </div>
  );
}
